import { useRef, useState, useMemo } from 'react';

const EditModal = ({
  isOpen,
  onClose,
  title,
  icon: Icon,
  fields = [],
  onSave,
  loading
}) => {
  const modalOverlayRef = useRef(null);
  const [errors, setErrors] = useState({});

  const REQUIRED_FIELDS = useMemo(() => new Set([
    'first name',
    'last name',
    'street address',
    'city',
    'zip code',
    'enter refund amount'
  ]), []);

  const shouldValidateField = (field) => {
    const placeholder = field.placeholder?.toLowerCase();
    return placeholder && REQUIRED_FIELDS.has(placeholder);
  };

  if (!isOpen) return null;

  const validateFields = () => {
    const newErrors = {};
    fields.forEach((field, idx) => {
      if (shouldValidateField(field)) {
        const value = field.value || '';
        if (!value.trim()) {
          newErrors[idx] = `${field.placeholder || 'This field'} is required`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateFields()) {
      onSave();
    }
  };

  const clearFieldError = (idx) => {
    if (errors[idx]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[idx];
        return newErrors;
      });
    }
  };

  return (
    <div style={{ opacity: 1, display: 'block' }} className="hide-me-overlay-2">
      <div className="modal-scroll-2" ref={modalOverlayRef}>
        <div className="close-overlay"
          onClick={() => {
            if (!loading) {
              setErrors({});
              onClose();
            }
          }}
        ></div>
        <div className="upgrade-container-2">

          {/* Header */}
          <div className="div-block-123">
            <div className="icon right-margin w-embed">
              {Icon && <Icon />}
            </div>
            <div className="text-block-28">{title}</div>
          </div>

          {/* Dynamic Fields */}
          {fields.map((field, idx) => {
            const shouldValidate = shouldValidateField(field);
            const hasError = shouldValidate && errors[idx];

            if (field.type === "select") {
              // Render a dropdown for state
              return (
                <div key={idx} className="div-block-122">
                  <select
                    key={idx}
                    value={field.value || ""}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      clearFieldError(idx);
                    }}
                    required={field.required}
                    className={`text-field input centered w-input edit-modal-select ${hasError ? 'error' : ''}`}
                  >
                    <option value="">{field.placeholder || "Select an option"}</option>
                    {field.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {hasError && (
                    <span style={{ color: '#d43737', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {errors[idx]}
                    </span>
                  )}
                </div>
              );
            }

            // Default: render a text input
            return (
              <div key={idx} className="div-block-122">
                <input
                  key={idx}
                  type="text"
                  placeholder={field.placeholder}
                  value={field.value}
                  maxLength={200}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (inputValue.length <= 200) {
                      field.onChange(inputValue);
                      clearFieldError(idx);
                    }
                  }}
                  required={field.required}
                  className={`text-field input centered w-input ${hasError ? 'error' : ''}`}
                  style={hasError ? { borderColor: '#d43737' } : {}}
                />
                {hasError && (
                  <span style={{ color: '#d43737', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors[idx]}
                  </span>
                )}
                <span
                  style={{
                    color: field.value?.length >= 200 ? '#d43737' : '#666',
                    fontWeight: field.value?.length >= 200 ? 'bold' : 'normal'
                  }}
                >
                  {field.value?.length >= 200 && "200 chracters Limit Reached!"}
                </span>
              </div>
            );
          })}

          {/* Actions */}
          <div className="w-layout-grid grid-button-notes">
            <button
              type="button"
              className="_2nd-button main small transparent w-inline-block"
              onClick={() => {
                setErrors({});
                onClose();
              }}
              disabled={loading}
              style={{ opacity: (loading ? 0.6 : 1), cursor: loading ? "not-allowed" : "pointer" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="_2nd-button main small w-inline-block"
              onClick={handleSave}
              disabled={loading}
              style={{ cursor: loading ? "not-allowed" : "pointer" }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
